function checkFrameRate(options, metaData) {
	if (!options.frameRate?.max && !options.frameRate?.min) {
		return false;
	}
	return (
		!metaData.avg_frame_rate ||
		metaData.avg_frame_rate > options.frameRate.max ||
		metaData.avg_frame_rate < options.frameRate.min
	);
}

function checkAspectRatio(options, width, height) {
	if (!options.aspect?.min || !options.aspect?.max) {
		return false;
	}
	const fileaspect = width / height;
	return fileaspect < options.aspect.min || fileaspect > options.aspect.max;
}

function checkVideoContentType(options, file) {
	return (
		options.mediaType === 'video' &&
		file.contentType &&
		!file.contentType.toLowerCase().startsWith('video/')
	);
}

function checkVideoCodec(options, metaData) {
	return (
		options.videoCodecNames?.length && !options.videoCodecNames.includes(metaData.video_codec_name)
	);
}

function checkAudioCodec(options, metaData) {
	return (
		options.audioCodecNames?.length && !options.audioCodecNames.includes(metaData.audio_codec_name)
	);
}

function getImageValidationResult(ctx) {
	const { options, src, width, height, filesize, mediaTypeCapital, $i18next } = ctx;
	const contentType = src.contentType?.toLowerCase();

	const validations = [
		{
			check: () => {
				if (!options.aspect || !options.aspect.min || !options.aspect.max) {
					return false;
				}
				const fileaspect = width / height;
				const validAspect = options.aspect.min <= fileaspect && fileaspect <= options.aspect.max;
				return !validAspect;
			},
			error: 'general.media_upload.wrong_aspect_ratio',
			userAction: true
		},
		{
			check: () => options.maxHeight && height > options.maxHeight,
			error: 'general.media_upload.too_big_height',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.maxWidth && width > options.maxWidth,
			error: 'general.media_upload.too_big_width',
			params: () => ({ mediaType: mediaTypeCapital })
		},

		{
			check: () => width < options.minWidth || height < options.minHeight,
			error: 'general.media_upload.too_small',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.maxFileSize && filesize && filesize > options.maxFileSize,
			error: 'general.media_upload.file_too_big',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () =>
				options.mediaType === 'image' && contentType && !contentType.startsWith('image/'),
			error: 'general.media_upload.filetype_not_valid',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.minFileSize && filesize && filesize < options.minFileSize,
			error: 'general.media_upload.file_too_small',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.fileType === 'jpg' && src.contentType !== 'image/jpeg',
			error: 'general.media_upload.filetype_not_valid',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () =>
				options.fileType === 'jpg,png' &&
				src.contentType !== 'image/jpeg' &&
				src.contentType !== 'image/png',
			error: 'general.media_upload.filetype_not_valid',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => contentType === 'image/gif',
			error: 'general.media_upload.filetype_not_valid',
			params: () => ({ mediaType: mediaTypeCapital })
		}
	];

	let result = { error: '', userAction: false };
	for (const validation of validations) {
		if (validation.check()) {
			result = {
				error: $i18next.t(validation.error, validation.params?.()),
				userAction: validation.userAction || false
			};
		}
	}
	return result;
}

function getVideoValidationError(ctx) {
	const { options, metaData, file, metrics, mediaTypeCapital, $i18next } = ctx;
	const { width, height, duration, video_bitrate, audio_bitrate, filesize } = metrics;

	const validations = [
		{
			check: () => options.maxVideoBitrate && video_bitrate > options.maxVideoBitrate,
			error: 'general.media_upload.video_bitrate_too_high'
		},
		{
			check: () => options.maxAudioBitrate && audio_bitrate > options.maxAudioBitrate,
			error: 'general.media_upload.audio_bitrate_too_high'
		},
		{
			check: () =>
				options.maxAudioSampleRate &&
				metaData.audio_sample_rate &&
				metaData.audio_sample_rate > options.maxAudioSampleRate,
			error: 'general.media_upload.audio_sample_rate_too_high'
		},
		{
			check: () => checkFrameRate(options, metaData),
			error: 'general.media_upload.frame_rate_wrong',
			params: () => ({ frameRate: `${options.frameRate?.min}-${options.frameRate?.max}` })
		},
		{
			check: () => checkAspectRatio(options, width, height),
			error: 'general.media_upload.wrong_aspect_ratio'
		},
		{
			check: () => options.maxHeight && height > options.maxHeight,
			error: 'general.media_upload.too_big_height',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.maxWidth && width > options.maxWidth,
			error: 'general.media_upload.too_big_width',
			params: () => ({ mediaType: mediaTypeCapital })
		},

		{
			check: () => width < options.minWidth || height < options.minHeight,
			error: 'general.media_upload.too_small',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.maxFileSize && filesize > options.maxFileSize,
			error: 'general.media_upload.file_too_big',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.minFileSize && filesize < options.minFileSize,
			error: 'general.media_upload.file_too_small',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => options.minDuration && duration < options.minDuration,
			error: 'general.media_upload.video_too_short'
		},
		{
			check: () => options.maxDuration && duration > options.maxDuration,
			error: 'general.media_upload.video_too_long'
		},
		{
			check: () => checkVideoContentType(options, file),
			error: 'general.media_upload.filetype_not_valid',
			params: () => ({ mediaType: mediaTypeCapital })
		},
		{
			check: () => checkVideoCodec(options, metaData),
			error: 'general.media_upload.video_codec_wrong',
			params: () => ({ codecs: options.videoCodecNames?.filter(r => r).join(', ') })
		},
		{
			check: () => checkAudioCodec(options, metaData),
			error: 'general.media_upload.audio_codec_wrong',
			params: () => ({ codecs: options.audioCodecNames?.filter(r => r).join(', ') })
		},
		{
			check: () => options.noEditList && metaData.hasEditList,
			error: 'general.media_upload.has_edit_list'
		}
	];

	let error = '';
	for (const validation of validations) {
		if (validation.check()) {
			error = $i18next.t(validation.error, validation.params?.());
		}
	}
	return error;
}

export function Validator(
	$i18next,
	options = {
		// for both image and video:
		mediaType: 'image',
		fileType: '',
		aspect: { min: 0, max: 0 },
		maxWidth: 0,
		maxHeight: 0,
		minWidth: 0,
		minHeight: 0,
		maxFileSize: 0,
		minFileSize: 0,

		// only for video:
		maxDuration: 0,
		minDuration: 0,
		maxVideoBitrate: 0,
		maxAudioBitrate: 0,
		videoCodecNames: [],
		audioCodecNames: [],
		maxAudioSampleRate: 0,
		frameRate: {
			max: 0,
			min: 0
		},
		noEditList: false
	}
) {
	const validateImage = (
		src = {
			imgData: null,
			contentType: '',
			size: 0,
			metaData: {
				width: 0,
				height: 0
			}
		}
	) => {
		// pass this to react component
		src.error = '';
		src.userAction = false;
		let width;
		let height;
		let filesize;

		const mediaTypeCapital = $i18next.t('general.image_plain');

		if (src.imgData) {
			// means it's onModidy
			({ height, width } = src.imgData);
		} else {
			const meta = src.metaData || src.meta;
			if (!meta) {
				src.error = $i18next.t('posts.draft.media_meta_missing');
				src.isValid = false;
				return false;
			}
			({ width, height } = meta);
			filesize = src.size || src.fileSize;
		}

		const result = getImageValidationResult({
			options,
			src,
			width,
			height,
			filesize,
			mediaTypeCapital,
			$i18next
		});

		src.error = result.error;
		src.userAction = result.userAction;
		src.isValid = !src.error;
		return !src.error;
	};
	const validateVideo = (
		file = {
			contentType: '',
			size: 0,
			metaData: {
				width: 0,
				height: 0,
				duration: 0,
				video_bitrate: 0,
				audio_bitrate: 0,
				video_codec_name: '',
				audio_codec_name: '',
				audio_sample_rate: 0,
				avg_frame_rate: 0,
				hasEditList: false
			}
		}
	) => {
		const metaData = file.metaData || file.meta;
		if (!metaData) {
			// old imported posts are missing metaData
			file.error = $i18next.t('posts.draft.media_meta_missing');
			file.isValid = false;
			return false;
		}
		const { width, height, duration, video_bitrate, audio_bitrate } = metaData;
		const filesize = file.size || file.fileSize;
		const mediaTypeCapital = $i18next.t('general.video_plain');

		file.error = getVideoValidationError({
			options,
			metaData,
			file,
			metrics: { width, height, duration, video_bitrate, audio_bitrate, filesize },
			mediaTypeCapital,
			$i18next
		});

		file.isValid = !file.error;
		return !file.error;
	};

	return { validateImage, validateVideo };
}

if (window.angular) {
	window.angular.module('PinMeTo.services').service('pmtMediaService', [
		'$i18next',
		function ($i18next) {
			this.Validator = Validator.bind(this, $i18next);
		}
	]);
}
